import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, MoreVertical, PlusSquare, Share } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

const DISMISS_KEY = 'admin-install-dismissed';

type Platform = 'ios' | 'android' | null;

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  const isIpadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/i.test(ua) || isIpadOs) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return null;
}

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

function Step({ n, title, children }: { n: string; title: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-[12px] border border-line-2 bg-surface-2 p-3.5">
      <span className="font-display text-2xl leading-none font-bold text-brand">{n}</span>
      <div className="flex flex-col gap-1 text-sm">
        <span className="font-semibold text-ink">{title}</span>
        <span className="text-[13px] leading-[18px] text-muted">{children}</span>
      </div>
    </div>
  );
}

/** Convida a instalar o /admin na tela inicial (iPhone e Android). */
export function InstallAppDialog() {
  const [platform, setPlatform] = useState<Platform>(null);
  const [open, setOpen] = useState(false);
  const [promptEvent, setPromptEvent] = useState<InstallPromptEvent | null>(null);

  useEffect(() => {
    const detected = detectPlatform();
    if (!detected || isStandalone() || localStorage.getItem(DISMISS_KEY)) return;
    setPlatform(detected);
    setOpen(true);

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setPromptEvent(e as InstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setOpen(false);
  };

  const install = async () => {
    if (!promptEvent) return;
    await promptEvent.prompt();
    await promptEvent.userChoice;
    dismiss();
  };

  if (!platform) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : dismiss())}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Instale o app Enchei-vos</DialogTitle>
          <DialogDescription>
            Acesse o painel direto da tela inicial do seu{' '}
            {platform === 'ios' ? 'iPhone' : 'Android'}, sem abrir o navegador.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2.5">
          {platform === 'ios' ? (
            <>
              <Step n="01" title="Toque em Compartilhar">
                No Safari, toque no ícone <Share className="inline size-3.5" /> na barra
                inferior.
              </Step>
              <Step n="02" title="Adicionar à Tela de Início">
                Role e escolha <PlusSquare className="inline size-3.5" /> “Adicionar à Tela
                de Início”.
              </Step>
              <Step n="03" title="Confirme">
                Toque em “Adicionar”. O app aparece na sua tela inicial.
              </Step>
            </>
          ) : (
            <>
              <Step n="01" title="Abra o menu do navegador">
                No Chrome, toque nos três pontinhos <MoreVertical className="inline size-3.5" />
                .
              </Step>
              <Step n="02" title="Instalar app">
                Escolha “Instalar app” ou “Adicionar à tela inicial”.
              </Step>
              <Step n="03" title="Confirme">
                Toque em “Instalar”. O app aparece na sua tela inicial.
              </Step>
            </>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {promptEvent && (
            <Button size="lg" onClick={install}>
              <Download /> Instalar agora
            </Button>
          )}
          <Button variant="outline" size="lg" onClick={dismiss}>
            Agora não
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
